import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  fetchCommunityCategories, fetchCommunityFeed, fetchComments, createPost, uploadImage,
  toggleReaction, addComment, deleteComment, reportPost,
  reportComment, approvePost, hidePost, unhidePost, moderatorDeletePost,
  fetchMe, isModerator, CurrentUser,
} from '../lib/community-api';

const reactions = [
  { type: 'like', emoji: '❤️' },
  { type: 'love', emoji: '😍' },
  { type: 'fire', emoji: '🔥' },
  { type: 'clap', emoji: '👏' },
  { type: 'muscle', emoji: '💪' },
];

const badgeColors: Record<string, string> = {
  moderator: 'bg-blue-500',
  expert: 'bg-green-500',
  ambassador: 'bg-yellow-500',
  admin: 'bg-red-500',
};

const badgeLabels: Record<string, string> = {
  moderator: 'MOD',
  expert: 'EXPERT',
  ambassador: 'AMBASSADOR',
  admin: 'ADMIN',
};

interface Props {
  isLoggedIn: boolean;
}

export default function Community({ isLoggedIn }: Props) {
  const [categories, setCategories] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Create post
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostCategory, setNewPostCategory] = useState<number | null>(null);
  const [newPostImage, setNewPostImage] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<string | null>(null);

  // Comments
  const [expandedPost, setExpandedPost] = useState<number | null>(null);
  const [comments, setComments] = useState<Record<number, any[]>>({});
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);

  // Reactions
  const [showReactions, setShowReactions] = useState<number | null>(null);

  // Current user (for moderator gating)
  const [me, setMe] = useState<CurrentUser | null>(null);
  const iAmMod = isModerator(me);

  // Last post just created (to show pending banner if not auto-approved)
  const [pendingNotice, setPendingNotice] = useState<string | null>(null);

  useEffect(() => {
    fetchCommunityCategories().then(setCategories).catch(() => {});
    if (isLoggedIn) fetchMe().then(setMe).catch(() => {});
  }, [isLoggedIn]);

  useEffect(() => {
    loadFeed();
  }, [page, selectedCategory]);

  const loadFeed = async () => {
    setLoading(true);
    try {
      const res = await fetchCommunityFeed(page, 20, selectedCategory || undefined);
      setPosts(res.data || []);
      setPagination(res.pagination);
    } catch {}
    finally { setLoading(false); }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) return;
    setCreating(true);
    setCreateResult(null);
    try {
      let imageId;
      if (newPostImage) {
        const uploaded = await uploadImage(newPostImage);
        imageId = uploaded.id;
      }
      const res = await createPost({
        content: newPostContent.trim(),
        category: newPostCategory || undefined,
        images: imageId ? [imageId] : undefined,
      });
      const approved = res?.data?.attributes?.is_approved ?? res?.data?.is_approved;
      if (approved === false) {
        setPendingNotice('Your post is awaiting moderator review and will appear once approved.');
        setCreateResult(null);
      } else {
        setPendingNotice(null);
        setCreateResult('Post published!');
        loadFeed();
      }
      setNewPostContent('');
      setNewPostImage(null);
      setShowCreatePost(false);
    } catch {
      setCreateResult('Failed to create post. Please try again.');
    }
    finally { setCreating(false); }
  };

  const handleReaction = async (postId: number, type: string) => {
    if (!isLoggedIn) return;
    try {
      const res = await toggleReaction(postId, type);
      setPosts(posts.map(p => p.id === postId ? {
        ...p,
        likes_count: res.likes_count,
        user_reaction: res.reacted ? res.reaction_type : null,
      } : p));
      setShowReactions(null);
    } catch {}
  };

  const loadComments = async (postId: number) => {
    try {
      const res = await fetchComments(postId);
      setComments(prev => ({ ...prev, [postId]: res.data || [] }));
    } catch {}
  };

  const handleToggleComments = (postId: number) => {
    if (expandedPost === postId) {
      setExpandedPost(null);
    } else {
      setExpandedPost(postId);
      if (!comments[postId]) loadComments(postId);
    }
  };

  const handleAddComment = async (postId: number) => {
    if (!commentText.trim() || commenting) return;
    setCommenting(true);
    try {
      const res = await addComment(postId, commentText.trim());
      setComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), res.comment],
      }));
      setPosts(posts.map(p => p.id === postId ? { ...p, comments_count: res.comments_count } : p));
      setCommentText('');
    } catch {}
    finally { setCommenting(false); }
  };

  const handleDeleteComment = async (postId: number, commentId: number) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteComment(commentId);
      setComments(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).filter(c => c.id !== commentId),
      }));
      setPosts(posts.map(p => p.id === postId ? { ...p, comments_count: Math.max((p.comments_count || 0) - 1, 0) } : p));
    } catch {}
  };

  const handleReport = async (postId: number) => {
    const reason = window.prompt('Why are you reporting this post?');
    if (reason === null) return;
    try {
      await reportPost(postId, reason);
      alert('Post reported. Thank you for keeping the community safe.');
    } catch {}
  };

  const handleReportComment = async (commentId: number) => {
    const reason = window.prompt('Why are you reporting this comment?');
    if (reason === null) return;
    try {
      await reportComment(commentId, reason || undefined);
      alert('Comment reported.');
    } catch {}
  };

  const handleHidePost = async (postId: number) => {
    if (!window.confirm('Hide this post from the feed?')) return;
    try {
      await hidePost(postId);
      setPosts((list) => list.filter((p) => p.id !== postId));
    } catch { alert('Failed to hide post.'); }
  };

  const handleApprovePost = async (postId: number) => {
    try {
      await approvePost(postId);
      setPosts((list) => list.map((p) => (p.id === postId ? { ...p, is_approved: true } : p)));
    } catch { alert('Failed to approve post.'); }
  };

  const handleModeratorDelete = async (postId: number) => {
    if (!window.confirm('Permanently delete this post and all its comments?')) return;
    try {
      await moderatorDeletePost(postId);
      setPosts((list) => list.filter((p) => p.id !== postId));
    } catch { alert('Failed to delete post.'); }
  };

  const userId = localStorage.getItem('userId');

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Community — EatMeat | Carnivore & Ketovore Members</title>
        <meta name="description" content="Join the EatMeat community. Share your carnivore or ketovore journey, post transformations, swap recipes, and get inspired by fellow members." />
        <link rel="canonical" href="https://www.eatmeatapp.com/community" />
      </Helmet>
      {/* Page header strip */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 md:py-5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🍖</span>
            <div>
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">EatMeat Community</h1>
              <p className="text-gray-500 text-xs">Share recipes, track progress, connect with carnivores & ketovores</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats row on neutral background */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700">
            <span>👥</span> {posts.length} Posts
          </div>
          <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700">
            <span>📂</span> {categories.length} Categories
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {pendingNotice && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center justify-between">
            <span>⏳ {pendingNotice}</span>
            <button className="text-amber-700 hover:text-amber-900 text-xs font-semibold" onClick={() => setPendingNotice(null)}>Dismiss</button>
          </div>
        )}
        {iAmMod && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800 flex items-center justify-between">
            <span>You have moderator privileges in this community.</span>
            <Link to="/community/moderation" className="text-blue-700 hover:text-blue-900 text-xs font-semibold underline">Open queue →</Link>
          </div>
        )}
        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4">
          <button
            onClick={() => { setSelectedCategory(null); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategory ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            All Posts
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.id ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>

        {/* Create Post */}
        {isLoggedIn && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            {!showCreatePost ? (
              <button
                onClick={() => setShowCreatePost(true)}
                className="w-full text-left text-gray-400 px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                What's on your mind? Share with the community...
              </button>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="Share your carnivore journey, recipes, tips..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  rows={4}
                  autoFocus
                />
                <div className="flex gap-3 items-center">
                  <select
                    value={newPostCategory || ''}
                    onChange={(e) => setNewPostCategory(e.target.value ? Number(e.target.value) : null)}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                  <label className="cursor-pointer px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                    📷 {newPostImage ? newPostImage.name : 'Add Photo'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setNewPostImage(e.target.files?.[0] || null)} />
                  </label>
                  <div className="flex-1" />
                  <button onClick={() => { setShowCreatePost(false); setNewPostContent(''); setNewPostImage(null); }} className="text-gray-400 text-sm hover:text-gray-600">Cancel</button>
                  <button
                    onClick={handleCreatePost}
                    disabled={!newPostContent.trim() || creating}
                    className="px-6 py-2 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {creating ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </div>
            )}
            {createResult && (
              <p className={`mt-3 text-sm ${createResult.includes('Failed') ? 'text-red-500' : 'text-green-500'}`}>
                {createResult}
              </p>
            )}
          </div>
        )}

        {/* Feed */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg">No posts yet</p>
            <p className="text-sm mt-1">Be the first to share something!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <div key={post.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Post Header */}
                <div className="px-4 pt-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                    {(post.user?.username || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{post.user?.username || 'Anonymous'}</span>
                      {post.user?.community_badge && post.user.community_badge !== 'member' && (
                        <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${badgeColors[post.user.community_badge] || 'bg-gray-400'}`}>
                          {badgeLabels[post.user.community_badge] || post.user.community_badge}
                        </span>
                      )}
                      {post.is_pinned && <span className="text-xs text-yellow-500">📌 Pinned</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      {post.category && <span>• {post.category.icon} {post.category.name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isLoggedIn && String(post.user?.id) !== userId && (
                      <button onClick={() => handleReport(post.id)} className="text-gray-300 hover:text-gray-500 text-sm" title="Report">
                        🚩
                      </button>
                    )}
                    {iAmMod && (
                      <>
                        {post.is_approved === false && (
                          <button onClick={() => handleApprovePost(post.id)} className="text-xs px-2 py-1 rounded-md bg-green-50 text-green-700 hover:bg-green-100 font-semibold" title="Approve">
                            Approve
                          </button>
                        )}
                        <button onClick={() => handleHidePost(post.id)} className="text-xs px-2 py-1 rounded-md bg-yellow-50 text-yellow-800 hover:bg-yellow-100 font-semibold" title="Hide">
                          Hide
                        </button>
                        <button onClick={() => handleModeratorDelete(post.id)} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold" title="Delete">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Post Content */}
                <div className="px-4 py-3">
                  <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                </div>

                {/* Post Images */}
                {post.images && post.images.length > 0 && (
                  <div className="px-4 pb-3">
                    <div className={`grid gap-2 ${post.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {post.images.map((img: any, i: number) => (
                        <img
                          key={i}
                          src={img.url?.startsWith('http') ? img.url : `https://www.eatmeatapp.com${img.url}`}
                          alt=""
                          className="w-full rounded-lg object-cover max-h-80"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Reactions & Comments Bar */}
                <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4">
                  {/* Like button + count */}
                  <div className="relative">
                    <button
                      onClick={() => isLoggedIn && (showReactions === post.id ? setShowReactions(null) : setShowReactions(post.id))}
                      onDoubleClick={() => isLoggedIn && handleReaction(post.id, 'like')}
                      className={`flex items-center gap-1 text-sm transition-colors ${post.user_reaction ? 'text-primary font-medium' : 'text-gray-500 hover:text-primary'}`}
                    >
                      {post.user_reaction ? reactions.find(r => r.type === post.user_reaction)?.emoji || '❤️' : '🤍'}
                      <span>{post.likes_count || 0}</span>
                    </button>
                    {/* Reaction picker */}
                    {showReactions === post.id && (
                      <div className="absolute bottom-8 left-0 bg-white rounded-full shadow-lg border border-gray-200 flex gap-1 px-2 py-1 z-10">
                        {reactions.map(r => (
                          <button
                            key={r.type}
                            onClick={() => handleReaction(post.id, r.type)}
                            className={`text-xl hover:scale-125 transition-transform p-1 ${post.user_reaction === r.type ? 'scale-125' : ''}`}
                            title={r.type}
                          >
                            {r.emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Comment button */}
                  <button
                    onClick={() => handleToggleComments(post.id)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary transition-colors"
                  >
                    💬 <span>{post.comments_count || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedPost === post.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    {/* Comment List */}
                    <div className="space-y-3 py-3">
                      {(comments[post.id] || []).length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-2">No comments yet</p>
                      ) : (
                        (comments[post.id] || []).map((comment: any) => (
                          <div key={comment.id} className="flex gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0 mt-0.5">
                              {(comment.user?.username || '?')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{comment.user?.username || 'Anonymous'}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400">
                                    {new Date(comment.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                  {isLoggedIn && String(comment.user?.id) !== userId && (
                                    <button onClick={() => handleReportComment(comment.id)} className="text-xs text-gray-300 hover:text-amber-500" title="Report comment">
                                      🚩
                                    </button>
                                  )}
                                  {(String(comment.user?.id) === userId || iAmMod) && (
                                    <button onClick={() => handleDeleteComment(post.id, comment.id)} className="text-xs text-gray-300 hover:text-red-400" title={iAmMod && String(comment.user?.id) !== userId ? 'Delete as moderator' : 'Delete'}>✕</button>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5">{comment.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Add Comment */}
                    {isLoggedIn && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={expandedPost === post.id ? commentText : ''}
                          onChange={(e) => setCommentText(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                          placeholder="Write a comment..."
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                        />
                        <button
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentText.trim() || commenting}
                          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                          {commenting ? '...' : 'Post'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pageCount > 1 && (
          <div className="flex justify-center gap-3 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-30 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-500">
              Page {page} of {pagination.pageCount}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.pageCount, p + 1))}
              disabled={page >= pagination.pageCount}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-30 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Login prompt */}
        {!isLoggedIn && (
          <div className="mt-8 text-center bg-white rounded-xl border border-gray-200 p-8">
            <p className="text-gray-500 mb-3">Log in to post, comment, and react</p>
            <a href="/login" className="inline-block px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90">
              Log In
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
